import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { ArrowDown, ArrowUp, ClipboardList, EyeOff, Pencil, Plus, Undo2 } from "lucide-react";
import { dashboardToast } from '../../utils/dashboardToast';
import Menu from '../components/menu';
import DashboardShell from '../../components/DashboardShell';
import { useMenuState } from "../../Common/MenuStateContext";
import ProfileInfo from '../components/profile-info';
import RecordDetailModal from '../../components/RecordDetailModal';
import {
  getAssessmentTemplateVersion,
  getAssessmentTemplateVersionUsage,
  listAssessmentTemplates,
  publishAssessmentTemplateVersion,
  rebuildAssessmentTemplateResolutions,
  restoreAssessmentTemplateVersionDefaults,
  saveAssessmentTemplateVersion,
} from '../../services/assessmentTemplateApi';
import type {
  AssessmentTemplateSummary,
  AssessmentTemplateVersionUsage,
} from '../../types/dynamicAssessment';
import {
  addMaturityOption,
  buildDraftFromPublishedVersion,
  buildSaveTemplateVersionRequest,
  createEditorStateFromVersion,
  deactivateField,
  deactivateOption,
  isFieldInUse,
  isOptionInUse,
  getOptionUsageCount,
  reorderOptions,
  softDeleteOption,
  updateField,
  updateOption,
  updateSection,
  validateTemplateEditor,
  type TemplateEditorState,
} from '../../utils/assessmentTemplateAdminUtils';
import '../../assets/css/dashboard.css';

const AssessmentTemplateManagement = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [templates, setTemplates] = useState<AssessmentTemplateSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editor, setEditor] = useState<TemplateEditorState | null>(null);
  const [usage, setUsage] = useState<AssessmentTemplateVersionUsage | null>(null);
  const [activeTab, setActiveTab] = useState('maturity');
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRecord, setViewRecord] = useState<AssessmentTemplateSummary | null>(null);
  const dataFetchedRef = useRef(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listAssessmentTemplates();
      setTemplates(result);
    } catch (error) {
      dashboardToast.error(error instanceof Error ? error.message : 'Failed to load templates.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (dataFetchedRef.current) {
      return;
    }
    dataFetchedRef.current = true;
    fetchTemplates();
  }, [fetchTemplates]);

  const openEditor = useCallback(async (template: AssessmentTemplateSummary) => {
    if (!template.latestVersionId) {
      dashboardToast.error('This template does not have a version to edit.');
      return;
    }

    setLoading(true);
    try {
      let version = await getAssessmentTemplateVersion(template.latestVersionId);

      if (version.status === 'Published') {
        const draftPayload = buildDraftFromPublishedVersion(version);
        version = await saveAssessmentTemplateVersion(draftPayload);
        dashboardToast.info(`Created draft v${version.versionNumber} from published version.`);
      }

      const editorState = createEditorStateFromVersion(version);
      const usageData = await getAssessmentTemplateVersionUsage(version.id);
      setEditor(editorState);
      setUsage(usageData);
      setActiveTab('maturity');
      setEditorOpen(true);
    } catch (error) {
      dashboardToast.error(error instanceof Error ? error.message : 'Failed to open template editor.');
    } finally {
      setLoading(false);
    }
  }, []);

  const closeEditor = () => {
    setEditorOpen(false);
    setEditor(null);
    setUsage(null);
    setRestoreConfirmOpen(false);
  };

  const openViewModal = useCallback((template: AssessmentTemplateSummary) => {
    setViewRecord(template);
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewRecord(null);
  }, []);

  const handleSaveDraft = async () => {
    if (!editor) {
      return;
    }

    const validationError = validateTemplateEditor(editor);
    if (validationError) {
      dashboardToast.error(validationError);
      return;
    }

    setLoading(true);
    try {
      const saved = await saveAssessmentTemplateVersion(buildSaveTemplateVersionRequest(editor));
      const editorState = createEditorStateFromVersion(saved);
      const usageData = await getAssessmentTemplateVersionUsage(saved.id);
      setEditor(editorState);
      setUsage(usageData);
      dashboardToast.success('Draft saved successfully.');
      fetchTemplates();
    } catch (error) {
      dashboardToast.error(error instanceof Error ? error.message : 'Failed to save draft.');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!editor?.versionId) {
      return;
    }

    const validationError = validateTemplateEditor(editor);
    if (validationError) {
      dashboardToast.error(validationError);
      return;
    }

    setLoading(true);
    try {
      await saveAssessmentTemplateVersion(buildSaveTemplateVersionRequest(editor));
      const published = await publishAssessmentTemplateVersion(editor.versionId);
      await rebuildAssessmentTemplateResolutions({});
      dashboardToast.success(`Published v${published.versionNumber} and rebuilt resolution cache.`);
      closeEditor();
      fetchTemplates();
    } catch (error) {
      dashboardToast.error(error instanceof Error ? error.message : 'Failed to publish template.');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreDefaults = async () => {
    if (!editor?.versionId) {
      return;
    }

    setLoading(true);
    try {
      const restored = await restoreAssessmentTemplateVersionDefaults(editor.versionId, {
        changeNotes: 'Restored system defaults',
      });
      const editorState = createEditorStateFromVersion(restored);
      const usageData = await getAssessmentTemplateVersionUsage(restored.id);
      setEditor(editorState);
      setUsage(usageData);
      setRestoreConfirmOpen(false);
      dashboardToast.success('Template restored to system defaults.');
    } catch (error) {
      dashboardToast.error(error instanceof Error ? error.message : 'Failed to restore defaults.');
    } finally {
      setLoading(false);
    }
  };

  const maturitySetIndex = 0;

  return (
    <DashboardShell isMenuCollapsed={isMenuCollapsed}
      sidebar={<Menu onSelect={setSelectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />}>

      <div className="dash-frame-parent sa-modal-host">
        <div className="dash-frame-group">
          <div className="dash-dashboard app-heading-accent text-[var(--app-heading)]" style={{ marginBottom: "15px" }}>
            Assessment Template Management
          </div>
          <ProfileInfo />
        </div>

        <div className="dash-your-project-parent">
          <div className="dash-your-project">Templates</div>
          <Button
            variant="outline-secondary"
            className="ms-auto"
            size="sm"
            onClick={fetchTemplates}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>

        <div className="dash-frame-wrapper">
          <div className="dash-frame-div">
            <div className="dash-frame-parent1" style={{ display: 'flex', flexDirection: 'column' }}>
              <table className="table table-striped mt-3">
                <thead style={{ fontSize: '0.9rem' }}>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Latest Version</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center">
                        {loading ? 'Loading templates...' : 'No assessment templates found.'}
                      </td>
                    </tr>
                  ) : (
                    templates.map((template, index) => (
                      <tr
                        key={template.id}
                        className="rdm-row-clickable"
                        onClick={() => openViewModal(template)}
                      >
                        <td>{index + 1}</td>
                        <td>
                          {template.name}
                          {template.isSystemDefault && (
                            <Badge bg="secondary" className="ms-2">
                              System
                            </Badge>
                          )}
                        </td>
                        <td>{template.assessmentTypeCode}</td>
                        <td>v{template.latestVersionNumber ?? '-'}</td>
                        <td>{template.latestVersionStatus ?? '-'}</td>
                        <td
                          className="bg-light"
                          style={{ whiteSpace: 'nowrap' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            className="btn btn-dashboard-primary btn-list-edit"
                            size="sm"
                            onClick={() => openEditor(template)}
                            disabled={loading}
                          >
                            <Pencil className="me-1" />
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Modal show={editorOpen} onHide={closeEditor} centered size="xl" dialogClassName="rounded-3">
        <Modal.Header closeButton className="dashboard-modal-header">
          <ClipboardList className="me-2" />
          <Modal.Title>
            {editor ? `${editor.templateName} — Draft v${editor.versionNumber}` : 'Template Editor'}
          </Modal.Title>
        </Modal.Header>
        {editor && (
          <>
            <Modal.Body className="px-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <Row className="mb-3">
                <Col md={8}>
                  <div className="text-muted small">
                    Edit maturity options and assessment questions. Items used in saved assessments
                    can be deactivated but not removed.
                  </div>
                  {usage && usage.responseInstanceCount > 0 && (
                    <Badge bg="warning" text="dark" className="mt-2">
                      {usage.responseInstanceCount} assessment instance(s) reference this version
                    </Badge>
                  )}
                </Col>
                <Col md={4} className="text-md-end">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="me-2"
                    onClick={() => setRestoreConfirmOpen(true)}
                    disabled={loading}
                  >
                    <Undo2 className="me-1" />
                    Restore Defaults
                  </Button>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Change Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={editor.changeNotes ?? ''}
                  onChange={(e) =>
                    setEditor({ ...editor, changeNotes: e.target.value })
                  }
                />
              </Form.Group>

              <Tab.Container activeKey={activeTab} onSelect={(key) => setActiveTab(key || 'maturity')}>
                <Nav variant="tabs" className="mb-3">
                  <Nav.Item>
                    <Nav.Link eventKey="maturity">Maturity Options</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="questions">Questions & Sections</Nav.Link>
                  </Nav.Item>
                </Nav>

                <Tab.Content>
                  <Tab.Pane eventKey="maturity">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        {editor.optionSets[maturitySetIndex]?.name ?? 'Maturity Scale'}
                      </h6>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setEditor(addMaturityOption(maturitySetIndex, editor))}
                      >
                        <Plus className="me-1" />
                        Add Option
                      </Button>
                    </div>

                    <table className="table table-sm table-striped">
                      <thead>
                        <tr>
                          <th>Order</th>
                          <th>Value</th>
                          <th>Label</th>
                          <th>Definition</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(editor.optionSets[maturitySetIndex]?.options ?? []).map((option, optionIndex) => {
                          const inUse = isOptionInUse(option.optionValue, usage ?? undefined);
                          const usageCount = getOptionUsageCount(option.optionValue, usage ?? undefined);
                          const isInactive = option.isActive === false;

                          return (
                            <tr key={`${option.optionValue}-${optionIndex}`} className={isInactive ? 'table-secondary' : ''}>
                              <td style={{ width: '90px' }}>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="me-1"
                                  disabled={optionIndex === 0}
                                  onClick={() =>
                                    setEditor(reorderOptions(maturitySetIndex, optionIndex, 'up', editor))
                                  }
                                >
                                  <ArrowUp />
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  disabled={
                                    optionIndex ===
                                    (editor.optionSets[maturitySetIndex]?.options?.length ?? 0) - 1
                                  }
                                  onClick={() =>
                                    setEditor(reorderOptions(maturitySetIndex, optionIndex, 'down', editor))
                                  }
                                >
                                  <ArrowDown />
                                </Button>
                              </td>
                              <td style={{ width: '70px' }}>{option.optionValue}</td>
                              <td style={{ width: '160px' }}>
                                <Form.Control
                                  size="sm"
                                  value={option.label}
                                  onChange={(e) =>
                                    setEditor(
                                      updateOption(editor, maturitySetIndex, optionIndex, {
                                        label: e.target.value,
                                      })
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  size="sm"
                                  value={option.definition ?? ''}
                                  onChange={(e) =>
                                    setEditor(
                                      updateOption(editor, maturitySetIndex, optionIndex, {
                                        definition: e.target.value,
                                      })
                                    )
                                  }
                                />
                              </td>
                              <td style={{ width: '120px' }}>
                                {isInactive ? (
                                  <Badge bg="secondary">Inactive</Badge>
                                ) : (
                                  <Badge bg="success">Active</Badge>
                                )}
                                {inUse && (
                                  <Badge bg="warning" text="dark" className="ms-1">
                                    Used ({usageCount})
                                  </Badge>
                                )}
                              </td>
                              <td style={{ width: '120px' }}>
                                {isInactive ? (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() =>
                                      setEditor(
                                        updateOption(editor, maturitySetIndex, optionIndex, {
                                          isActive: true,
                                        })
                                      )
                                    }
                                  >
                                    Activate
                                  </Button>
                                ) : inUse ? (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => {
                                      const result = deactivateOption(
                                        maturitySetIndex,
                                        optionIndex,
                                        editor
                                      );
                                      if (result.error) {
                                        dashboardToast.error(result.error);
                                        return;
                                      }
                                      setEditor(result.editor);
                                    }}
                                  >
                                    <EyeOff className="me-1" />
                                    Deactivate
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => {
                                      const result = softDeleteOption(
                                        maturitySetIndex,
                                        optionIndex,
                                        editor,
                                        usage ?? undefined
                                      );
                                      if (result.error) {
                                        dashboardToast.error(result.error);
                                        return;
                                      }
                                      setEditor(result.editor);
                                    }}
                                  >
                                    Delete
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Tab.Pane>

                  <Tab.Pane eventKey="questions">
                    {editor.sections.map((section, sectionIndex) => (
                      <div key={section.code} className="border rounded p-3 mb-3">
                        <Row className="mb-2">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="required">Section Title</Form.Label>
                              <Form.Control
                                value={section.title}
                                onChange={(e) =>
                                  setEditor(
                                    updateSection(editor, sectionIndex, { title: e.target.value })
                                  )
                                }
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Section Code</Form.Label>
                              <Form.Control value={section.code} disabled />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Form.Group className="mb-3">
                          <Form.Label>Section Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={section.description ?? ''}
                            onChange={(e) =>
                              setEditor(
                                updateSection(editor, sectionIndex, {
                                  description: e.target.value,
                                })
                              )
                            }
                          />
                        </Form.Group>

                        {section.fields.map((field, fieldIndex) => {
                          const inUse = isFieldInUse(field.fieldCode, usage ?? undefined);
                          const isInactive = field.isActive === false;

                          return (
                            <div
                              key={field.fieldCode}
                              className={`border rounded p-3 mb-2 ${isInactive ? 'bg-light' : ''}`}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                  <strong>{field.fieldCode}</strong>
                                  {inUse && (
                                    <Badge bg="warning" text="dark" className="ms-2">
                                      Used in assessments
                                    </Badge>
                                  )}
                                  {isInactive && (
                                    <Badge bg="secondary" className="ms-2">
                                      Inactive
                                    </Badge>
                                  )}
                                </div>
                                <div>
                                  {isInactive ? (
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() =>
                                        setEditor(
                                          updateField(editor, sectionIndex, fieldIndex, {
                                            isActive: true,
                                          })
                                        )
                                      }
                                    >
                                      Activate
                                    </Button>
                                  ) : inUse ? (
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() =>
                                        setEditor(deactivateField(editor, sectionIndex, fieldIndex))
                                      }
                                    >
                                      <EyeOff className="me-1" />
                                      Deactivate
                                    </Button>
                                  ) : null}
                                </div>
                              </div>

                              <Row>
                                <Col md={6}>
                                  <Form.Group className="mb-2">
                                    <Form.Label className="required">Question Label</Form.Label>
                                    <Form.Control
                                      value={field.label}
                                      onChange={(e) =>
                                        setEditor(
                                          updateField(editor, sectionIndex, fieldIndex, {
                                            label: e.target.value,
                                          })
                                        )
                                      }
                                    />
                                  </Form.Group>
                                </Col>
                                <Col md={6}>
                                  <Form.Group className="mb-2">
                                    <Form.Label>Placeholder</Form.Label>
                                    <Form.Control
                                      value={field.placeholder ?? ''}
                                      onChange={(e) =>
                                        setEditor(
                                          updateField(editor, sectionIndex, fieldIndex, {
                                            placeholder: e.target.value,
                                          })
                                        )
                                      }
                                    />
                                  </Form.Group>
                                </Col>
                              </Row>
                              <Form.Group>
                                <Form.Label>Help Text</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  value={field.helpText ?? ''}
                                  onChange={(e) =>
                                    setEditor(
                                      updateField(editor, sectionIndex, fieldIndex, {
                                        helpText: e.target.value,
                                      })
                                    )
                                  }
                                />
                              </Form.Group>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={closeEditor} disabled={loading}>
                Cancel
              </Button>
              <Button
                className="btn btn-dashboard-primary"
                onClick={handleSaveDraft}
                disabled={loading}
              >
                Save Draft
              </Button>
              <Button variant="success" onClick={handlePublish} disabled={loading}>
                Publish
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <Modal show={restoreConfirmOpen} onHide={() => setRestoreConfirmOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Restore System Defaults</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will replace the current draft with the system default maturity scale, sections, and
          questions. Items used in assessments will remain protected on save. Continue?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRestoreConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="warning" onClick={handleRestoreDefaults} disabled={loading}>
            Restore Defaults
          </Button>
        </Modal.Footer>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        title={viewRecord?.name || 'Template Details'}
        subtitle="Assessment template"
        fields={[
          { label: 'Name', value: viewRecord?.name },
          { label: 'Description', value: viewRecord?.description, fullWidth: true },
          { label: 'Assessment Type', value: viewRecord?.assessmentTypeCode },
          {
            label: 'Latest Version',
            value:
              viewRecord?.latestVersionNumber != null
                ? `v${viewRecord.latestVersionNumber}`
                : undefined,
          },
          { label: 'Latest Version Status', value: viewRecord?.latestVersionStatus },
          { label: 'Scope Level', value: viewRecord?.scopeLevel },
          {
            label: 'System Default',
            value: viewRecord?.isSystemDefault ? 'Yes' : 'No',
          },
          { label: 'Active', value: Boolean(viewRecord?.isActive) },
        ]}
        actions={[
          {
            type: 'edit',
            onClick: () => {
              const template = viewRecord;
              closeViewModal();
              if (template) openEditor(template);
            },
            disabled: loading,
          },
        ]}
      />
    </DashboardShell>
  );
};

export default AssessmentTemplateManagement;
